import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchIntradayResults, scanIntradayStocks, calcOrh, updateVwap, runIntradayStrategies } from "../services/scannerService";
import useWebSocket from "./useWebSocket";

export default function useIntradayScanner(date?: string) {
  const [intradayResults, setIntradayResults] = useState<any[]>([]);
  const [scanning, setScanning] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const { registerHandler, unregisterHandler } = useWebSocket();

  const loadData = async () => {
    try {
      const results = await fetchIntradayResults(date);
      setIntradayResults(results);
    } catch (err) {
      console.error("Failed to load intraday results", err);
    }
  };

  useEffect(() => {
    loadData();

    // Register WebSocket handler for real-time updates
    const handlerId = "intraday-scanner-hook";
    registerHandler("INTRADAY_SCAN_COMPLETE", handlerId, (data: any) => {
      console.log("WebSocket: Intraday scan completed event received", data);
      toast.success(`📊 Scan complete! Found ${data.count} qualifiers.`);
      loadData();
      setScanning(false);
    });

    return () => {
      unregisterHandler("INTRADAY_SCAN_COMPLETE", handlerId);
    };
  }, [date]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await scanIntradayStocks();
      toast.info("🚀 Intraday scan triggered! Checking status...");
    } catch (err) {
      setScanning(false);
      toast.error("❌ Failed to trigger intraday scan");
    }
  };

  const orhTrigger = async () => {
    setLoadingAction("orh");
    try {
      await calcOrh();
      toast.success("✅ Opening Range calculated!");
      await loadData();
    } catch (err: any) {
      toast.error(`❌ ORH failed: ${err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const vwapTrigger = async () => {
    setLoadingAction("vwap");
    try {
      await updateVwap();
      toast.success("✅ VWAP updated!");
      await loadData();
    } catch (err: any) {
      toast.error(`❌ VWAP failed: ${err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const strategyTrigger = async () => {
    setLoadingAction("strategy");
    try {
      await runIntradayStrategies();
      toast.success("✅ Strategy signals evaluated!");
    } catch (err: any) {
      toast.error(`❌ Strategy engine failed: ${err.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  return {
    intradayResults,
    triggerScan,
    orhTrigger,
    vwapTrigger,
    strategyTrigger,
    scanning,
    loadingAction,
    refreshResults: loadData
  };
}
